import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Card } from '@/components/shared';

export default function AdminParams() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/params').then((r) => setP(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        customerDiscount: parseFloat(p.customerDiscount),
        referralRate: parseFloat(p.referralRate),
        referralSalesCount: parseInt(p.referralSalesCount, 10),
        referralMaxInvites: parseInt(p.referralMaxInvites, 10),
        clubCardStampsForBonus: parseInt(p.clubCardStampsForBonus, 10),
        clubCardBonus: parseFloat(p.clubCardBonus),
        passiveDays: parseInt(p.passiveDays, 10),
        commissionHoldDays: parseInt(p.commissionHoldDays, 10),
        payoutDayOfMonth: parseInt(p.payoutDayOfMonth, 10),
        reminderHoursBefore: parseInt(p.reminderHoursBefore, 10),
        tiers: p.tiers.map((t) => ({
          name: t.name, rate: parseFloat(t.rate), threshold: parseFloat(t.threshold),
          perks: Array.isArray(t.perks)
            ? t.perks
            : String(t.perks || '').split('\n').map((x) => x.trim()).filter(Boolean),
        })),
        referralBonusTiers: (p.referralBonusTiers || []).map((t) => ({ minInvites: parseInt(t.minInvites, 10), extraRate: parseFloat(t.extraRate) })),
      };
      const r = await api.put('/admin/params', payload);
      setP(r.data);
      toast.success('Parametreler kaydedildi');
    } catch { toast.error('Kaydetme başarısız'); }
    finally { setSaving(false); }
  };

  const updTier = (i, field, val) => {
    const tiers = [...p.tiers];
    tiers[i] = { ...tiers[i], [field]: val };
    setP({ ...p, tiers });
  };

  const updRefTier = (i, field, val) => {
    const tiers = [...(p.referralBonusTiers || [])];
    tiers[i] = { ...tiers[i], [field]: val };
    setP({ ...p, referralBonusTiers: tiers });
  };
  const addRefTier = () => setP({ ...p, referralBonusTiers: [...(p.referralBonusTiers || []), { minInvites: 0, extraRate: 0 }] });
  const removeRefTier = (i) => setP({ ...p, referralBonusTiers: (p.referralBonusTiers || []).filter((_, idx) => idx !== i) });

  if (!p) return <div className="text-navy/40 text-sm">Yükleniyor...</div>;

  return (
    <div className="fade-up max-w-3xl" data-testid="admin-params">
      <h1 className="font-serif text-3xl text-navy mb-1">Program Parametreleri</h1>
      <p className="text-navy/50 text-sm mb-6">Oran, eşik ve indirim ayarları</p>

      {/* Tiers */}
      <Card className="p-6 mb-5">
        <h2 className="font-serif text-lg text-navy mb-4">Seviyeler & Komisyon</h2>
        <div className="space-y-3" data-testid="params-tiers">
          {p.tiers.map((t, i) => (
            <div key={t.name} className="border-b border-navy/10 pb-4 last:border-0 last:pb-0">
              <div className="grid grid-cols-3 gap-3 items-center">
                <span className="text-sm font-medium text-navy">{t.name}</span>
                <div>
                  <label className="text-xs text-navy/50 block mb-1">Komisyon oranı</label>
                  <input
                    type="number" step="0.01"
                    value={t.rate}
                    onChange={(e) => updTier(i, 'rate', e.target.value)}
                    data-testid={`param-tier-rate-${i}`}
                    className="w-full bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold"
                  />
                </div>
                <div>
                  <label className="text-xs text-navy/50 block mb-1">Aylık ciro eşiği (₺)</label>
                  <input
                    type="number"
                    value={t.threshold}
                    onChange={(e) => updTier(i, 'threshold', e.target.value)}
                    data-testid={`param-tier-threshold-${i}`}
                    className="w-full bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold"
                  />
                </div>
              </div>
              <div className="mt-2">
                <label className="text-xs text-navy/50 block mb-1">Ayrıcalıklar (her satır bir ayrıcalık)</label>
                <textarea
                  rows={3}
                  value={Array.isArray(t.perks) ? t.perks.join('\n') : (t.perks || '')}
                  onChange={(e) => updTier(i, 'perks', e.target.value.split('\n'))}
                  data-testid={`param-tier-perks-${i}`}
                  className="w-full bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold resize-y"
                />
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-navy/40 mt-3">Oran ondalık girilir (örn. 0.06 = %6)</p>
      </Card>

      {/* Other params */}
      <Card className="p-6 mb-6">
        <h2 className="font-serif text-lg text-navy mb-4">Diğer Ayarlar</h2>
        <div className="grid grid-cols-2 gap-4">
          <NumField label="Müşteri indirimi (0.10 = %10)" step="0.01" value={p.customerDiscount} onChange={(v) => setP({ ...p, customerDiscount: v })} testid="param-discount" />
          <NumField label="Referans oranı (0.05 = %5)" step="0.01" value={p.referralRate} onChange={(v) => setP({ ...p, referralRate: v })} testid="param-referral-rate" />
          <NumField label="Referans satış sayısı" value={p.referralSalesCount} onChange={(v) => setP({ ...p, referralSalesCount: v })} testid="param-referral-count" />
          <NumField label="Maks. davet sayısı" value={p.referralMaxInvites} onChange={(v) => setP({ ...p, referralMaxInvites: v })} testid="param-max-invites" />
          <NumField label="Kulüp kartı damga sayısı" value={p.clubCardStampsForBonus} onChange={(v) => setP({ ...p, clubCardStampsForBonus: v })} testid="param-stamps" />
          <NumField label="Kulüp kartı bonusu (₺)" value={p.clubCardBonus} onChange={(v) => setP({ ...p, clubCardBonus: v })} testid="param-club-bonus" />
          <NumField label="Pasif eşiği (gün)" value={p.passiveDays} onChange={(v) => setP({ ...p, passiveDays: v })} testid="param-passive-days" />
          <NumField label="Komisyon onay süresi (gün)" value={p.commissionHoldDays} onChange={(v) => setP({ ...p, commissionHoldDays: v })} testid="param-hold-days" />
          <NumField label="Ödeme günü (ayın günü)" value={p.payoutDayOfMonth} onChange={(v) => setP({ ...p, payoutDayOfMonth: v })} testid="param-payout-day" />
          <NumField label="Etkinlik hatırlatma (saat önce)" value={p.reminderHoursBefore} onChange={(v) => setP({ ...p, reminderHoursBefore: v })} testid="param-reminder-hours" />
        </div>
      </Card>

      {/* Referral bonus tiers */}
      <Card className="p-6 mb-6" data-testid="params-referral-tiers">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-lg text-navy">Kademeli Davet Primi</h2>
          <button onClick={addRefTier} data-testid="add-ref-tier-btn" className="text-xs bg-navy text-cream px-3 py-1.5 rounded-md active:scale-95 transition-transform">+ Kademe ekle</button>
        </div>
        <p className="text-xs text-navy/50 mb-4">Belirtilen davet sayısına ulaşan mimarlara temel davet primine ek oran uygulanır.</p>
        <div className="space-y-3">
          {(p.referralBonusTiers || []).map((t, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end">
              <div>
                <label className="text-xs text-navy/50 block mb-1">Min. davet sayısı</label>
                <input type="number" value={t.minInvites} onChange={(e) => updRefTier(i, 'minInvites', e.target.value)} data-testid={`ref-tier-min-${i}`} className="w-full bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold" />
              </div>
              <div>
                <label className="text-xs text-navy/50 block mb-1">Ek oran (0.01 = %1)</label>
                <input type="number" step="0.01" value={t.extraRate} onChange={(e) => updRefTier(i, 'extraRate', e.target.value)} data-testid={`ref-tier-rate-${i}`} className="w-full bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold" />
              </div>
              <button onClick={() => removeRefTier(i)} data-testid={`remove-ref-tier-${i}`} className="text-xs border border-redx/30 text-redx px-3 py-2 rounded-md active:scale-95 transition-transform">Sil</button>
            </div>
          ))}
          {(!p.referralBonusTiers || p.referralBonusTiers.length === 0) && (
            <p className="text-sm text-navy/40">Kademe tanımlı değil.</p>
          )}
        </div>
      </Card>

      <button onClick={save} disabled={saving} data-testid="params-save-btn" className="bg-navy text-cream rounded-md px-6 py-3 text-sm font-medium active:scale-95 transition-transform disabled:opacity-50">
        {saving ? 'Kaydediliyor...' : 'Parametreleri Kaydet'}
      </button>
    </div>
  );
}

const NumField = ({ label, value, onChange, step, testid }) => (
  <div>
    <label className="text-xs text-navy/50 block mb-1">{label}</label>
    <input
      type="number" step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold"
    />
  </div>
);
